import * as THREE from "three";

/**
 * RoomBox - Configurable grey room walls around furniture
 *
 * Creates 4 grey walls forming a "room" around the scene.
 * Walls use FrontSide with inward-pointing normals, making them
 * visible only from inside (see-through from outside).
 */
const MIN_SIZE = 1;  // meters
const MAX_SIZE = 50; // meters

export class RoomBox {
  constructor(sceneManager) {
    this.sceneManager = sceneManager;

    this.walls = [];
    this.enabled = false;
    this.width = 6;  // meters
    this.depth = 8;  // meters
    this.height = 2.7; // meters (fixed wall height)

    this._material = null;
  }

  isEnabled() {
    return this.enabled;
  }

  _getMaterial() {
    if (!this._material) {
      this._material = new THREE.MeshStandardMaterial({
        color: 0xbcbcbc,
        roughness: 0.88,
        metalness: 0.0,
        side: THREE.FrontSide, // visible only from inside (normals face inward)
      });
    }
    return this._material;
  }

  show() {
    this.enabled = true;
    this._rebuild();
  }

  hide() {
    this.enabled = false;
    this._clearWalls();
  }

  /**
   * Set room dimensions, clamped to the minimum that fits all furniture.
   * Returns the actual dimensions applied (may be clamped).
   */
  setSize(width, depth, furnitureManager) {
    const min = this.getMinimumSize(furnitureManager);
    this.width = Math.min(MAX_SIZE, Math.max(MIN_SIZE, Math.max(min.width, width)));
    this.depth = Math.min(MAX_SIZE, Math.max(MIN_SIZE, Math.max(min.depth, depth)));

    if (this.enabled) {
      this._rebuild();
    }

    return { width: this.width, depth: this.depth };
  }

  /**
   * Compute the minimum box dimensions needed to contain all furniture
   * with a small padding margin.
   */
  getMinimumSize(furnitureManager) {
    if (!furnitureManager) return { width: 1, depth: 1 };

    const furniture = furnitureManager.getAllFurniture();
    if (!furniture || furniture.length === 0) return { width: 1, depth: 1 };

    const combined = new THREE.Box3();
    let hasContent = false;

    furniture.forEach((f) => {
      if (f.mesh) {
        const itemBox = new THREE.Box3().setFromObject(f.mesh);
        combined.union(itemBox);
        hasContent = true;
      }
    });

    if (!hasContent) return { width: 1, depth: 1 };

    const padding = 0.5; // 0.5 m on each side
    const minW = (combined.max.x - combined.min.x) + padding * 2;
    const minD = (combined.max.z - combined.min.z) + padding * 2;

    return {
      width: Math.max(1, isFinite(minW) ? minW : 1),
      depth: Math.max(1, isFinite(minD) ? minD : 1),
    };
  }

  _rebuild() {
    this._clearWalls();
    this._createWalls();
  }

  _createWalls() {
    const scene = this.sceneManager.getScene();
    if (!scene) return;

    const W = this.width;
    const D = this.depth;
    const H = this.height;
    const mat = this._getMaterial();

    // Each plane has its front face normal pointing INWARD (toward room center).
    // THREE.FrontSide = visible from inside only (transparent from outside).
    //
    // PlaneGeometry default normal = +Z.
    // Rotations below orient each wall so its front face looks toward the center:
    //   North wall  (z = -D/2): no rotation needed, normal = +Z (toward +Z = inside)
    //   South wall  (z = +D/2): rotate Y 180°, normal = -Z (toward -Z = inside)
    //   East wall   (x = +W/2): rotate Y -90°, normal = -X (toward -X = inside)
    //   West wall   (x = -W/2): rotate Y +90°, normal = +X (toward +X = inside)
    const wallDefs = [
      { w: W, h: H, pos: [0,      H / 2, -D / 2], rotY: 0,               name: "north" },
      { w: W, h: H, pos: [0,      H / 2,  D / 2], rotY: Math.PI,         name: "south" },
      { w: D, h: H, pos: [W / 2,  H / 2,  0    ], rotY: -Math.PI / 2,    name: "east"  },
      { w: D, h: H, pos: [-W / 2, H / 2,  0    ], rotY:  Math.PI / 2,    name: "west"  },
    ];

    wallDefs.forEach((def) => {
      const geo = new THREE.PlaneGeometry(def.w, def.h);
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.set(...def.pos);
      mesh.rotation.y = def.rotY;
      mesh.receiveShadow = true;
      mesh.name = `roombox_wall_${def.name}`;
      mesh.userData.isRoomBox = true;
      scene.add(mesh);
      this.walls.push(mesh);
    });
  }

  _clearWalls() {
    const scene = this.sceneManager.getScene();
    this.walls.forEach((w) => {
      if (scene) scene.remove(w);
      if (w.geometry) w.geometry.dispose();
    });
    this.walls = [];
  }

  dispose() {
    this._clearWalls();
    if (this._material) {
      this._material.dispose();
      this._material = null;
    }
  }
}
